import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GridHelperService {
  constructor() { }

  // Full word replacements
  private fieldOverrides: Record<string, string> = {
    theSubjectsAssessmentAndTaxesAre: 'Assessment and Taxes',
  };

  // Known acronyms & replacements
  private acronymMap: Record<string, string> = {
    id: 'ID',
    psf: 'PSF',
    cr: 'CR',
    mr: 'MR',
    qoq: 'QoQ',
    y15: 'Y1-5',
    y1to5: 'Y1-5',
    y1_5: 'Y1-5',
    y1: 'Y1',
    walt: 'WALT',
    uspap: 'USPAP',
  };

  /**
   * Attempts to match embedded acronyms inside keys like "qoqCrdelta"
   */
  private smartTokenize(key: string): string[] {
    // Step 1: normalize snake_case to camelCase style
    let normalized = key.replace(/_/g, '');

    // Step 2: insert space before capital letters or numbers following lowercase
    normalized = normalized.replace(/([a-z])([A-Z0-9])/g, '$1 $2');

    // Step 3: insert space before acronym patterns (e.g. QoQ, PSF, CR, etc.)
    Object.keys(this.acronymMap).forEach(acronym => {
      const pattern = new RegExp(acronym, 'ig');
      normalized = normalized.replace(pattern, ` ${this.acronymMap[acronym]} `);
    });

    // Step 4: split, trim, capitalize everything else
    return normalized
      .split(/\s+/)
      .map((word, i, arr) => {
        const clean = word.trim().toLowerCase();

        // Combine Y1 and 5 into Y1-5
        if ((clean === 'y1') && (arr[i + 1]?.trim() === '5')) {
          arr[i + 1] = ''; // Remove next token
          return 'Y1-5';
        }

        return this.acronymMap[clean] || this.capitalize(clean);
      })
      .filter(w => w); // remove blanks
  }

  private capitalize(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  private toReadableLabel(key: string): string {
    if (this.fieldOverrides[key]) {
      return this.fieldOverrides[key];
    }

    return this.smartTokenize(key).join(' ');
  }

  /**
   * Recursively flattens nested objects and returns readable, distinct headers
   */
  flattenObject(obj: any): string[] {
    const fieldSet = new Set<string>();

    const recurse = (currentObj: any) => {
      Object.keys(currentObj || {}).forEach((key) => {
        const value = currentObj[key];

        if (value && typeof value === 'object' && !Array.isArray(value)) {
          recurse(value);
        } else {
          const readable = this.toReadableLabel(key);
          fieldSet.add(readable);
        }
      });
    };

    recurse(obj);
    return Array.from(fieldSet).sort();
  }
}
const Utility = {
  paidBy: "Paid By",
  provider: "Provider"
}

export const valuationSummaryScenario = {
  interestAppraised: "Interest Appraised",
  valueType: "Value Type",
  valueCondition: "Value Condition",
  valueTiming: "Value Timing",
  dateOfValue: "Date of Value/Effective Date",
  priorDateOfValue: "Prior Date Of Value",
  landValue: "Land Value",
  excessLandValue: "Excess Land Value",
  insurableReplacementCost:"Insurable Replacement Cost",
  costApproachIndicatedValue: "Cost Approach Indicated Value",
  totalDepreciation: "Total Depreciation",
  salesComparisonApproachIndicatedValue: "Sales Comparison Approach Indicated Value",
  incomeCapitalizationApproach: "Income Capitalization Approach",
  holdingPeriod: "Hold Period/Investment Horizon",
  terminalCapitalizationRate: "Terminal Capitalization Rate",
  internalRateOfReturn: "Internal Rate of Return/Discount Rate",
  yieldCapitalizationIndicatedValue: "Yield Capitalization Indicated Value/DCF Indicated Value",
  capitalizationRate: "Capitalization Rate",
  directCapitalizationIndicatedValue: "Direct Capitalization Indicated Value",
  finalValue: "Final Value",
  priorFinalValue: "Prior Final Value",
  primaryValue: "Primary Value",  
  exposurePeriod:"Exposure Period",
  marketingPeriod:"Marketing Period",
}

export const revenueAndExpenseSection = {
  "baseRentalRevenue" : "Base Rental Revenue",
  "totalOtherIncome" : "Total Other Income",
  "potentialGrossRevenue": "Potential Gross Revenue",
  "vacancyandCollectionLoss": "Vacancy and Collection Loss",
  "effectiveGrossRevenue": "Effective Gross Revenue (Total Amount)",
  "insurance": "Insurance",  
  "generalnAdministrative": "General & Administrative",  
  "managementFees" : "Management Fees",
  "replacementReserves": "Replacement Reserves",
  "operatingExpenses": "Operating Expenses",  
  "realEstateTaxes": "Real Estate Taxes",
  "groundRent": "Ground Rent",
  "totalExpenses": "Total Expenses",
  "nOITotalAmount": "NOI (Total Amount)"
}

export const FieldDisplayNameMap = {
  activityNumber: "Activity Number",
  // externalReferenceId: "External Reference ID",
  timeStamp: "Timestamp",
  // rowkey: "Row Key",
  hasJobCenterInfo: "Has Job Center Info",
  // fileName: "File Name",
  submittedBy: "Submitted By",

  project: {
    projectName: "Project Name",
    portfolio: {
      // portfolioId: "Portfolio ID",
      portfolioName: "Portfolio Name",
      client: {
        // clientId: "Client ID",
        clientName: "Client Name"
      }
    }
  },

  subjectPropertyLocation: {
    // propertyId: "Property ID",
    propertyName: "Property Name",
    addressLine1: "Address",
    city: "City",
    county: "County",
    state: "State/Province",
    zipCode: "Zip Code/Postal Code",
    country: "Country",
    region: "Region",
    // metropolitanStatisticalArea: "Metropolitan Statistical Area",
    market: "Market",
    submarket: "Submarket",
    streetLocation: "Street Location",
    // latitude: "Latitude",
    // longitude: "Longitude"
  },

  // parcelInformation: {
  //   assessorsParcelNumber: "Assessors Parcel Identification"
  // },

  siteInformation: {
    // primaryLandMeasure: "Primary Land Area Measure",
    primaryLandValue: "Primary Land Area/Site Area",
    primaryLandExcess: "Primary Excess Land Area",
    // primaryLandUnit: "Primary Unit of Measure",
    // secondaryLandMeasure: "Secondary Land Area Measure",
    secondaryLandValue: "Secondary Land Area/Site Area",
    secondaryLandExcess: "Secondary Excess Land Area",
    // secondaryLandUnit: "Secondary Unit of Measure",
    // shape: "Shape",
    // topography: "Topography",
    // access: "Access",
    // visibility: "Visibility",
    // frontage: "Frontage",
    siteImprovements: "Site Improvements",
    groundLeases: "Ground Leases",
    groundLeaseDescription: "Ground Lease Description",
    electric: Utility,
    gas: Utility,
    refuse: Utility,
    sewer: Utility,
    water: Utility,
    telephone: Utility
  },

  ownership: {
    propertyOwnershipEntity: "Property Ownership Entity",
    soldInLastThreeYears: "Sold In Last Three Years",
    saleDate: "Sale Data",
    salePrice: "Sale Price",
    listPrice: "List Price",
    contractPrice: "Contract Price"
  },

  // siteConditions: {
  //   soilConditions: "Soil Conditions",
  //   landUseRestrictions: "Land Use Restrictions",
  //   wetlands: "Wetlands",
  //   hazardousSubstances: "Hazardous Substances",
  //   overallUtility: "Overall Utility",
  //   summary: "Summary"
  // },

  // floodZoneInformation: {
  //   floodMapNumber: "Flood Map Number",
  //   floodMapDate: "Flood Map Date",
  //   floodZone: "Flood Zone",
  //   floodZoneComment: "Flood Zone Comment"
  // },

  improvementInformation: {
    // primaryImprovementMeasure: "Primary Improvement Area Measure",
    primaryImprovementValue: "Primary Improvement Area",
    primaryImprovementUnit: "Primary Unit of Measure",
    primaryImprovementPriorValue: "Primary Improvement Prior Value",
    // secondaryImprovementMeasure: "Secondary Improvement Area Measure",
    secondaryImprovementValue: "Secondary Improvement Area",
    secondaryImprovementUnit: "Secondary Unit Of Measure",
    secondaryImprovementPriorValue: "Secondary Improvement Prior Value",
    // tertiaryImprovementMeasure: "Tertiary Improvement Area Measure",
    tertiaryImprovementValue: "Tertiary Improvement Area",
    tertiaryImprovementUnit: "Tertiary Unit Of Measure",
    tertiaryImprovementPriorValue: "Tertiary Improvement Prior Value",
    yearBuilt: "Year Built",
    yearRenovated: "Year Last Renovated",
    effectiveAge: "Effective Age",
    economicLife: "Economic Life",
    numberOfStories: "Number Of Stories",
    numberOfBuildings: "Number Of Buildings",
    BuildingCondition: "Building Condition",
    BuildingQuality: "Building Quality",    
    // propertyClass: "Property Class",
    occupancy: "Occupancy",
    priorOccupancy: "Prior Occupancy",
    vacancy: "Vacancy",
    priorVacancy: "Prior Vacancy"
  },
  improvementInformationIndustrial: {
    percentOfficeSpace: "Percent Office Space",
    clearCeilingHeight: "Clear Ceiling Height",
    numberOfDockHighDoors: "Number of Dock-High Doors",
    numberOfDriveInDoors: "Number of Drive-In Doors"
  },
  improvementInformationLiving: {
    numberOfUnits: "Number of Units",
    projectAmenities: "Project Amenities",
    unitAmenities: "Unit Amenities",
    landOwnershipInterest: "Land Ownership Interest"
  },
  improvementInformationRetail: {
    numberOfAnchors: "Number Of Anchors",
    totalAnchorsArea: "Total Anchors Area",
    totalAnchorsGrossLeasableAreaPercentage: "Total Anchors Gross Leasable Area Percentage"
  },
  constructionDetails: {
    basicConstruction: "Basic Construction",
    constructionStatus: "Construction Status",
    exteriorWalls: "Exterior Walls",
    roofType: "Roof Type",
    roofSurface: "Roof Surface"    
  },

  mechanicalDetails: {
    // hvac: "HVAC",
    // fireProtection: "Fire Protection",
    // security: "Security"
    numberofPassengerElevators: "Number of Passenger Elevators",
    numberofFreightElevators: "Number of Freight Elevators"
  },

  // parking: {
  //   parkingTypeOne: "Parking Type One",
  //   parkingTypeOneNoOfSpaces: "Parking Type One - No. of Spaces",
  //   parkingTypeTwo: "Parking Type Two",
  //   parkingTypeTwoNoOfSpaces: "Parking Type Two - No. of Spaces",
  //   parkingTypeThree: "Parking Type Three",
  //   parkingTypeThreeNoOfSpaces: "Parking Type Three - No. of Spaces"
  // },

  taxes: {
    currentTaxYear: "Current Tax Year",
    assessmentRatio: "Assessment Ratio",
    equalizationRate: "Equalization Rate",
    areTaxesCurrent: "Are Taxes Current",
    isThereAGrievanceUnderway: "Is There a Grievance Underway",
    theSubjectsAssessmentAndTaxesAre: "The Subject’s Assessment & Taxes Are",
    totalTaxRate: "Total Tax Rate",
    totalPropertyTaxes: "Total Property Taxes",
    monthTaxesDueWithoutPenalty: "Month Taxes Due Without Penalty",
    percentDiscountIfTaxBillPaidByDecember: "Discount % if Paid by December",
    totalTaxExemptions: "Total Tax Exemptions",
    totalAssessedValue: "Total Assessed Value",
    improvementsAssessedValue: "Improvements Assessed Value",
    landAssessedValue: "Land Assessed Value",
    assessorsMarketValue: "Assessor's Market Value"
  },

  zoning: {
    currentZoning: "Current Zoning",
    isCurrentUsePermitted: "Is Current Use Permitted",
    permittedUses: "Permitted Uses",
    prohibitedUses: "Prohibited Uses"
  },

  constructionCosts: {
    softCosts: "Soft Costs",
    furnitureFixturesandEquipment: "Furniture Fixtures and Equipment",
    siteImprovementCost: "Site Improvement Cost",
    buildingImprovementCosts: "Building Improvement Costs",
    totalEntrepreneurialProfit: "Total Entrepreneurial Profit"
  },

  cashFlowModel: {
    cashFlowModelName: "Cash Flow Model Type",
    portfolioName: "Portfolio Name",
    scenarioName: "Scenario Name",
    propertyName: "Property Name"
  },

  tenantInformation: {
    largestTenant: "Largest Tenant",
    largestTenantArea: "Largest Tenant Area",
    leaseExpires: "Largest Tenant Lease Expires",
    avgExpiries: "Avg expiries for yr 1-5 % (Per Annum)",
    numberOfTenants:"Number of Tenants"
  },

  cashflow: {
    // netOperationIncome: {
    //   year1: "Year 1 NOI",
    //   year2: "Year 2 NOI",
    //   year3: "Year 3 NOI",
    //   year4: "Year 4 NOI",
    //   year5: "Year 5 NOI",
    //   year6: "Year 6 NOI",
    //   year7: "Year 7 NOI",
    //   year8: "Year 8 NOI",
    //   year9: "Year 9 NOI",
    //   year10: "Year 10 NOI",
    //   year11: "Year 11 NOI"
    // },
    // impliedOverallRate: {
    //   year1: "Year 1 Yield",
    //   year2: "Year 2 Yield",
    //   year3: "Year 3 Yield",
    //   year4: "Year 4 Yield",
    //   year5: "Year 5 Yield",
    //   year6: "Year 6 Yield",
    //   year7: "Year 7 Yield",
    //   year8: "Year 8 Yield",
    //   year9: "Year 9 Yield",
    //   year10: "Year 10 Yield",
    //   year11: "Year 11 Yield"
    // },
    year1NOIPSF: "Year 1 NOI / SF",
    walt: "WALT",
    priorWalt: "Prior WALT",
    priorVacancy: "Prior Vacancy",
    downtime: "Downtime",
    // vacancy: "Stabilized Vacancy",
    collectionLoss: "Collection Loss",
    priorCollectionLoss: "Prior Collection Loss",
    concessions: "Concessions",
    priorConcessions: "Prior Concessions",
    realEstateTaxGrowthRate: "Real Estate Tax Growth Rate",
    netCashFlowAmount: "Net Cash Flow Amount"
  },

  cashflowLiving: {
    nonApartmentTotalPSF: "Non-Apartment Total / SF"
  },

  cashflowRetail: {
    rollingTwelveMonthsSalesPSF: "Rolling 12-Month Sales / SF"
  },
  
  revenueAndExpense: {
    yearOne: revenueAndExpenseSection,
    stabilizedYear: revenueAndExpenseSection
  },

  assumptions: {
    marketInflationYears1_5: "Market Inflation Years 1-5",
    priorMarketInflationYears1_5: "Prior Market Inflation Years 1-5",
    expenseInflationYears1_5: "Expense Inflation Years 1-5",
    priorExpenseInflationYears1_5: "Prior Expense Inflation Years 1-5"
  },

  leaseExpirations: {
    fiveYearCumulativeExpirationsPSF: "Five Year Cumulative Expirations / Square Feet"
  },

  leasingAssumptions: {
    leaseType: "Lease Type (Reimbursements)",
    marketLeaseTerm: "Market Lease Term",
    renewalProbability: "Renewal Probability",
    marketLeasingTenantImprovementsNewLeases: "Market Leasing Tenant Improvements New Leases",
    marketLeasingCommissionsNewLeases: "Market Leasing Commissions New Leases",
    year1TenantImprovements: "Year 1 Tenant Improvements",
    year1LeasingCommissions: "Year 1 Leasing Commissions",
    FreeRent: "Free Rent (Months)",
    ExpenseReimbursement: "Expense Reimbursement (Total Amount)"
  },

  contractMonthRent: {
    averageContractRentPSF: "Average Contract Rent / SF",
    averageMarketRentPSF: "Average Market Rent / SF",
    annualContractRentTotal: "Annual Contract Rent (Total Amount)",
    cwProjectionMonthlyMarketRent: "C&W Projection Monthly Market Rent",
    crmr: "Contract Rent:Market Rent",
    annualMarketRentTotal: "Annual Market Rent (Total Amount)",
    marketOccupancyPercent: 'Market Occupancy Percent'
  },
  valuations: {
    currency: "Currency",
    dateOfReport: "Date Of Report",
    dateOfInspection: "Date Of Inspection",
    firrea: "FIRREA",
    isUSPAPCompliant: "USPAP/CUSPAP Compliant",
    stabilized: "Stabilized",
    clientOwnership: "Client Ownership %",
    previouslyAppraised: "Previously Appraised",
    partner: "Partner"
  },
  valuationSummary: {
    asIsScenario: valuationSummaryScenario,
    uponCompletionScenario: valuationSummaryScenario,
    uponStabilizationScenario: valuationSummaryScenario
  }
};





